import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const DatabaseSettingsTab = () => {
  const [connectionStatus, setConnectionStatus] = useState('Connected');
  const [backupSettings, setBackupSettings] = useState({
    autoBackup: true,
    frequency: 'daily',
    retention: '30',
    location: 's3://cyberguard-backups/'
  });

  const databases = [
    {
      id: 1,
      name: "Primary Database",
      type: "PostgreSQL",
      host: "db-primary.cyberguard.gov",
      port: "5432",
      status: "Connected",
      lastBackup: "2025-09-19 02:00:00",
      size: "2.4 GB",
      connections: 45
    },
    {
      id: 2,
      name: "Analytics Database",
      type: "PostgreSQL",
      host: "db-analytics.cyberguard.gov",
      port: "5432",
      status: "Connected",
      lastBackup: "2025-09-19 02:15:00",
      size: "8.7 GB",
      connections: 12
    },
    {
      id: 3,
      name: "Archive Database",
      type: "PostgreSQL",
      host: "db-archive.cyberguard.gov",
      port: "5432",
      status: "Maintenance",
      lastBackup: "2025-09-18 02:00:00",
      size: "45.2 GB",
      connections: 0
    }
  ];

  const performanceMetrics = [
    {
      metric: "Query Response Time",
      value: "12ms",
      status: "Good",
      trend: "stable"
    },
    {
      metric: "Active Connections",
      value: "57/100",
      status: "Good",
      trend: "up"
    },
    {
      metric: "Database Size",
      value: "56.3 GB",
      status: "Warning",
      trend: "up"
    },
    {
      metric: "Backup Success Rate",
      value: "99.8%",
      status: "Excellent",
      trend: "stable"
    }
  ];

  const backupHistory = [
    {
      id: 1,
      database: "Primary Database",
      timestamp: "2025-09-19 02:00:00",
      size: "2.4 GB",
      duration: "4m 32s",
      status: "Success",
      type: "Full"
    },
    {
      id: 2,
      database: "Analytics Database",
      timestamp: "2025-09-19 02:15:00",
      size: "8.7 GB",
      duration: "12m 18s",
      status: "Success",
      type: "Full"
    },
    {
      id: 3,
      database: "Primary Database",
      timestamp: "2025-09-18 02:00:00",
      size: "2.3 GB",
      duration: "4m 28s",
      status: "Success",
      type: "Full"
    },
    {
      id: 4,
      database: "Archive Database",
      timestamp: "2025-09-18 02:00:00",
      size: "45.1 GB",
      duration: "45m 12s",
      status: "Failed",
      type: "Full"
    }
  ];

  const frequencyOptions = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const retentionOptions = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '365', label: '1 Year' }
  ];

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'Connected': case'Success': case'Good': case'Excellent':
        return `${baseClasses} bg-success text-success-foreground`;
      case 'Warning':
        return `${baseClasses} bg-warning text-warning-foreground`;
      case 'Maintenance': case'Failed':
        return `${baseClasses} bg-error text-error-foreground`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <Icon name="TrendingUp" size={14} className="text-success" />;
      case 'down':
        return <Icon name="TrendingDown" size={14} className="text-error" />;
      default:
        return <Icon name="Minus" size={14} className="text-muted-foreground" />;
    }
  };

  const handleTestConnection = (dbId) => {
    console.log('Testing connection for database:', dbId);
  };

  const handleBackupNow = (dbId) => {
    console.log('Starting backup for database:', dbId);
  };

  return (
    <div className="space-y-8">
      {/* Database Connections */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Database" size={20} className="mr-2" />
              Database Connections
            </h3>
            <p className="text-sm text-muted-foreground">Monitor and manage database connections</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left">
            Refresh Status
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">Database</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Connection</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Last Backup</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Size</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {databases?.map((db) => (
                <tr key={db?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-foreground">{db?.name}</p>
                      <p className="text-sm text-muted-foreground">{db?.type}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-foreground">{db?.host}:{db?.port}</p>
                      <p className="text-xs text-muted-foreground">{db?.connections} connections</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={getStatusBadge(db?.status)}>
                      {db?.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-foreground">{new Date(db.lastBackup)?.toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">{new Date(db.lastBackup)?.toLocaleTimeString()}</p>
                  </td>
                  <td className="p-4 text-sm text-foreground">{db?.size}</td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="Zap"
                        onClick={() => handleTestConnection(db?.id)}
                      />
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="HardDrive"
                        onClick={() => handleBackupNow(db?.id)}
                      />
                      <Button variant="ghost" size="sm" iconName="Settings" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Performance Metrics */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Activity" size={20} className="mr-2" />
              Performance Metrics
            </h3>
            <p className="text-sm text-muted-foreground">Real-time database performance monitoring</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {performanceMetrics?.map((metric, index) => (
            <div key={index} className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{metric?.metric}</span>
                {getTrendIcon(metric?.trend)}
              </div>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-foreground">{metric?.value}</span>
                <span className={getStatusBadge(metric?.status)}>
                  {metric?.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Backup Configuration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="HardDrive" size={20} className="mr-2" />
              Backup Configuration
            </h3>
            <p className="text-sm text-muted-foreground">Configure automated backup settings</p>
          </div>
          <Button variant="default" iconName="Save" iconPosition="left">
            Save Settings
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable Automatic Backups"
              description="Automatically backup databases on schedule"
              checked={backupSettings?.autoBackup}
              onChange={(e) => setBackupSettings(prev => ({...prev, autoBackup: e?.target?.checked}))}
            />
            
            <Select
              label="Backup Frequency"
              options={frequencyOptions}
              value={backupSettings?.frequency}
              onChange={(value) => setBackupSettings(prev => ({...prev, frequency: value}))}
            />

            <Select
              label="Retention Period"
              options={retentionOptions}
              value={backupSettings?.retention}
              onChange={(value) => setBackupSettings(prev => ({...prev, retention: value}))}
            />

            <Input
              label="Backup Location"
              type="text"
              value={backupSettings?.location}
              onChange={(e) => setBackupSettings(prev => ({...prev, location: e?.target?.value}))}
              description="S3 bucket or file system path"
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Recent Backup History</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {backupHistory?.slice(0, 5)?.map((backup) => (
                <div key={backup?.id} className="p-3 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{backup?.database}</span>
                    <span className={getStatusBadge(backup?.status)}>
                      {backup?.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{new Date(backup.timestamp)?.toLocaleString()}</span>
                    <span>{backup?.size} • {backup?.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseSettingsTab;